import {
  TAG_COLOR_LABELS,
  TAG_COLORS,
  type LeadTagDto,
  type TagColor,
} from '@performance-crm/shared';
import { Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { TagChip } from '@/components/leads/TagChips';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { useCatalogs, useSetLeadTags, useUpsertTag } from '@/lib/queries';

/** Marcar las etiquetas de un lead; quien gestiona puede crear una nueva sin salir de la ficha. */
export function TagsDialog({
  leadId,
  companyId,
  current,
  canManage,
  onClose,
}: {
  leadId: string;
  companyId: string;
  current: LeadTagDto[];
  canManage: boolean;
  onClose: () => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const setTags = useSetLeadTags(leadId);
  const upsert = useUpsertTag();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set(current.map((t) => t.id)));
  const [name, setName] = useState('');
  const [color, setColor] = useState<TagColor>('azul');
  const tags = catalogs?.tags ?? [];
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const t = await upsert.mutateAsync({ companyId, name: name.trim(), color });
      setSelected((s) => new Set(s).add(t.id));
      setName('');
      toast.success('Etiqueta creada', t.name);
    } catch (err) {
      toast.error('No se pudo crear la etiqueta', (err as Error).message);
    }
  };
  const save = async () => {
    try {
      await setTags.mutateAsync([...selected]);
      toast.success('Etiquetas guardadas');
      onClose();
    } catch (err) {
      toast.error('No se pudieron guardar las etiquetas', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="tags-dialog">
        <DialogHeader>
          <DialogTitle>Etiquetas del lead</DialogTitle>
          <DialogDescription>
            Marca las que apliquen.{' '}
            {canManage
              ? 'Puedes crear una nueva aquí mismo.'
              : 'Las etiquetas nuevas las crea quien gestiona la empresa.'}
          </DialogDescription>
        </DialogHeader>
        {tags.length === 0 ? (
          <p className="text-muted-foreground text-sm">La empresa aún no tiene etiquetas.</p>
        ) : (
          <ul className="grid gap-1 sm:grid-cols-2">
            {tags.map((t) => (
              <li key={t.id}>
                <label className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selected.has(t.id)}
                    onChange={() => toggle(t.id)}
                  />
                  <TagChip tag={t} />
                </label>
              </li>
            ))}
          </ul>
        )}
        {canManage && (
          <form
            onSubmit={(e) => void create(e)}
            className="flex flex-wrap items-end gap-2 rounded-md border border-dashed p-2"
          >
            <div className="min-w-[160px] flex-1 space-y-1">
              <Label htmlFor="new-tag-name">Nueva etiqueta</Label>
              <Input
                id="new-tag-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                placeholder="Ej.: Inversionista"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-tag-color">Color</Label>
              <Select
                id="new-tag-color"
                value={color}
                onChange={(e) => setColor(e.target.value as TagColor)}
                className="w-32"
              >
                {TAG_COLORS.map((c) => (
                  <option key={c} value={c}>
                    {TAG_COLOR_LABELS[c]}
                  </option>
                ))}
              </Select>
            </div>
            <Button type="submit" variant="outline" disabled={!name.trim() || upsert.isPending}>
              <Plus className="h-4 w-4" /> Crear
            </Button>
          </form>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={setTags.isPending}
            data-testid="btn-save-tags"
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
